import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Bot, Loader2, RotateCcw, Send, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const SUGGESTIONS = ["What's my balance?", "When is my next payment?", "What's my SR22 filing status?", "How do I file a claim?"];
const newId = () => `s${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

const post = (body) => fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assistant/chat`, {
  method: "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
});

async function streamChat(body, onDelta) {
  let res = await post(body);
  if (res.status === 401) {
    await api.post("/auth/refresh");
    res = await post(body);
  }
  if (!res.ok) {
    const j = await res.json().catch(() => ({}));
    throw new Error(typeof j.detail === "string" ? j.detail : "The assistant is unavailable right now.");
  }
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const events = buf.split("\n\n");
    buf = events.pop();
    for (const ev of events) {
      if (!ev.startsWith("data: ")) continue;
      const d = JSON.parse(ev.slice(6));
      if (d.error) throw new Error(d.error);
      if (d.delta) onDelta(d.delta);
    }
  }
}

const Bubble = ({ m, i }) => (
  <div className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`} data-testid={`assistant-msg-${m.role}-${i}`}>
    <div className={`max-w-[85%] whitespace-pre-wrap rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-blue-700 text-white" : "bg-slate-100 text-slate-900"}`}>{m.content}</div>
  </div>
);

export const AssistantWidget = () => {
  const { user } = useAuth();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [sid, setSid] = useState(null);
  const end = useRef(null);
  const key = user ? `sd_chat_${user.id}` : null;

  useEffect(() => {
    if (!key) return;
    let s = localStorage.getItem(key);
    if (!s) { s = newId(); localStorage.setItem(key, s); }
    setSid(s);
    api.get(`/assistant/sessions/${s}`).then((r) => setMsgs(r.data)).catch(() => setMsgs([]));
  }, [key]);
  useEffect(() => { end.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, open]);

  if (!user || user.role === "admin" || loc.pathname.endsWith("/certificate")) return null;

  const send = async (msg) => {
    const m = (msg ?? text).trim();
    if (!m || busy) return;
    setText("");
    setBusy(true);
    setMsgs((x) => [...x, { role: "user", content: m }, { role: "assistant", content: "" }]);
    const append = (t) => setMsgs((x) => { const c = [...x]; c[c.length - 1] = { ...c[c.length - 1], content: c[c.length - 1].content + t }; return c; });
    try {
      await streamChat({ session_id: sid, message: m }, append);
    } catch (e) {
      setMsgs((x) => { const c = [...x]; c[c.length - 1] = { role: "assistant", content: e.message }; return c; });
    } finally {
      setBusy(false);
    }
  };
  const reset = () => { const s = newId(); localStorage.setItem(key, s); setSid(s); setMsgs([]); };

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} className="no-print fixed bottom-5 right-5 z-50 flex items-center gap-2 rounded-full bg-slate-900 px-5 py-3 text-sm font-semibold text-white shadow-xl transition-transform hover:-translate-y-0.5" data-testid="assistant-open-btn">
        <Bot className="h-5 w-5" /> Ask our assistant
      </button>
    );
  }
  return (
    <div className="no-print fixed inset-x-3 bottom-3 z-50 flex h-[70vh] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl sm:inset-x-auto sm:right-5 sm:w-[400px]" data-testid="assistant-panel">
      <div className="flex items-center justify-between bg-slate-900 px-4 py-3 text-white">
        <div className="flex items-center gap-2"><Bot className="h-5 w-5" /><div><p className="text-sm font-bold">Seutrade assistant</p><p className="text-[11px] text-slate-300">Answers from your account, 24/7</p></div></div>
        <div className="flex gap-1">
          <button onClick={reset} className="rounded p-1.5 hover:bg-white/10" aria-label="New conversation" data-testid="assistant-reset-btn"><RotateCcw className="h-4 w-4" /></button>
          <button onClick={() => setOpen(false)} className="rounded p-1.5 hover:bg-white/10" aria-label="Close" data-testid="assistant-close-btn"><X className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="assistant-messages">
        {!msgs.length && (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Hi {user.name.split(" ")[0]}, I can look up your policies, payments, filing status and claims. What can I help with?</p>
            <div className="flex flex-wrap gap-2">{SUGGESTIONS.map((s, i) => <button key={s} onClick={() => send(s)} className="rounded-full border border-slate-200 px-3 py-1 text-xs hover:border-blue-400 hover:text-blue-700" data-testid={`assistant-suggestion-${i}`}>{s}</button>)}</div>
          </div>
        )}
        {msgs.map((m, i) => (m.content || m.role === "user" ? <Bubble key={i} m={m} i={i} /> : <Loader2 key={i} className="h-4 w-4 animate-spin text-slate-400" />))}
        <div ref={end} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex gap-2 border-t border-slate-200 p-3">
        <input value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="Ask about your account…" className="flex-1 rounded-full border border-slate-300 px-4 py-2 text-sm outline-none focus:border-blue-500" data-testid="assistant-input" />
        <Button type="submit" size="icon" disabled={busy || !text.trim()} className="rounded-full bg-blue-700 hover:bg-blue-800" data-testid="assistant-send-btn"><Send className="h-4 w-4" /></Button>
      </form>
    </div>
  );
};
